import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route } from "react-router-dom";
import LoginPage from "@/pages/LoginPage";
import PostLogin from "@/pages/PostLogin";
import RespondentDashboard from "@/pages/RespondentDashboard";
import ClientAdminDashboard from "@/pages/ClientAdminDashboard";
import ClientAdminSubOrgs from "@/pages/ClientAdminSubOrgs";
import SubOrgPortfolios from "@/pages/SubOrgPortfolios";
import ReviewerInbox from "./pages/ReviewerInbox";
import ReviewerConsole from "./pages/ReviewerConsole";
import AdminConsole from "./pages/AdminConsole";
import OrgAdminConsole from "./pages/OrgAdminConsole";
import OrgUsers from "./pages/OrgUsers";
import OrgAssessments from "./pages/OrgAssessments";
import AssessmentOverview from "@/pages/AssessmentOverview";
import SubmissionViewer from "@/pages/SubmissionViewer";
import Wizard from "@/pages/Wizard";
import SelfAssessmentPdf from "@/reports/SelfAssessmentPdf";
import CapPreview from "@/pages/CapPreview";
import HrvReportPdf from "@/reports/HrvReportPdf";
import TriageReportPdf from "@/reports/TriageReportPdf";
import TelecomSelfReportPrint from "@/reports/pages/TelecomSelfReportPrint";
import ActionPlanView from "@/pages/ActionPlanView";
import PortfolioManagerDashboard from "@/pages/PortfolioManagerDashboard";
import AccountSecurity from "@/pages/AccountSecurity";
import AuthCallback from "@/pages/AuthCallback";
import AccessNotProvisioned from "@/pages/AccessNotProvisioned";
import SsoConsole from "@/pages/admin/SsoConsole";
import ApplicabilityRules from "@/pages/admin/ApplicabilityRules";
import AssessmentManifests from "@/pages/admin/AssessmentManifests";
import MethodologyCompositionConsole from "@/pages/admin/MethodologyCompositionConsole";

import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { RoleGate } from "@/components/auth/RoleGate";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useIdleLogout } from "@/hooks/useIdleLogout";
import { IdleWarningDialog } from "@/components/auth/IdleWarningDialog";

const queryClient = new QueryClient();

function IdleLogoutGuard() {
  const { showWarning, dismissWarning, logoutNow } = useIdleLogout();
  return (
    <IdleWarningDialog
      open={showWarning}
      onStay={dismissWarning}
      onLogout={logoutNow}
    />
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <IdleLogoutGuard />
        <Routes>
          {/* PUBLIC ROUTES - no auth required */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/access-not-provisioned" element={<AccessNotProvisioned />} />

          {/* Post-login: waits for roles then redirects */}
          <Route path="/post-login" element={<PostLogin />} />

          {/* Root: RoleGate handles redirects based on session/role */}
          <Route path="/" element={<RoleGate />} />

          {/* PRIVATE */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <RespondentDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client-admin"
            element={
              <ProtectedRoute roles={['client_admin','admin']}>
                <ClientAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/sub-orgs"
            element={
              <ProtectedRoute roles={['client_admin','admin']}>
                <ClientAdminSubOrgs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/sub-orgs/:subOrgId/portfolios"
            element={
              <ProtectedRoute roles={['client_admin','admin']}>
                <SubOrgPortfolios />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reviewer"
            element={
              <ProtectedRoute>
                <ReviewerInbox />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reviewer/:assessmentId"
            element={
              <ProtectedRoute roles={['reviewer','master_reviewer','admin']}>
                <ReviewerConsole />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reviewer/cap/:assessmentId/preview"
            element={
              <ProtectedRoute roles={['reviewer','master_reviewer','admin']}>
                <CapPreview />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminConsole />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sso"
            element={
              <ProtectedRoute roles={['admin']}>
                <SsoConsole />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/applicability"
            element={
              <ProtectedRoute roles={['admin']}>
                <ApplicabilityRules />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/assessment-dna"
            element={
              <ProtectedRoute roles={['admin']}>
                <AssessmentManifests />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/methodology-composition"
            element={
              <ProtectedRoute roles={['admin']}>
                <MethodologyCompositionConsole />
              </ProtectedRoute>
            }
          />

          <Route
            path="/org-admin"
            element={
              <ProtectedRoute roles={['org_admin','client_admin','admin']}>
                <OrgAdminConsole />
              </ProtectedRoute>
            }
          />
          <Route
            path="/org-admin/:orgId/users"
            element={
              <ProtectedRoute roles={['org_admin','client_admin','admin']}>
                <OrgUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/org-admin/:orgId/assessments"
            element={
              <ProtectedRoute roles={['portfolio_manager','org_admin','client_admin','admin']}>
                <OrgAssessments />
              </ProtectedRoute>
            }
          />

          <Route
            path="/assessment/:assessmentId"
            element={
              <ProtectedRoute>
                <AssessmentOverview />
              </ProtectedRoute>
            }
          />
          {/* Also support /assessments/:id/overview for PM dashboard navigation */}
          <Route
            path="/assessments/:assessmentId/overview"
            element={
              <ProtectedRoute roles={['portfolio_manager', 'admin', 'reviewer', 'master_reviewer', 'respondent', 'org_admin', 'client_admin']}>
                <AssessmentOverview />
              </ProtectedRoute>
            }
          />
          {/* Read-only questionnaire view for PM */}
          <Route
            path="/assessments/:assessmentId/questionnaire"
            element={
              <ProtectedRoute roles={['portfolio_manager', 'admin', 'reviewer', 'master_reviewer', 'org_admin', 'client_admin']}>
                <ErrorBoundary>
                  <Wizard />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* Editable wizard - exclude PM and client_admin (read-only roles) */}
          <Route
            path="/wizard/:assessmentId"
            element={
              <ProtectedRoute roles={['admin', 'reviewer', 'master_reviewer', 'respondent', 'org_admin', 'portfolio_manager']}>
                <ErrorBoundary>
                  <Wizard />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route path="/submission/:assessmentId" element={<SubmissionViewer />} />
          <Route
            path="/reports/self/:assessmentId"
            element={
              <ProtectedRoute>
                <SelfAssessmentPdf />
              </ProtectedRoute>
            }
          />
          <Route
            path="/print/telecom-self/:assessmentId"
            element={
              <ProtectedRoute>
                <TelecomSelfReportPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/hrv/:assessmentId"
            element={
              <ProtectedRoute>
                <HrvReportPdf />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/triage/:assessmentId"
            element={
              <ProtectedRoute>
                <TriageReportPdf />
              </ProtectedRoute>
            }
          />
          <Route
            path="/action-plan/:assessmentId"
            element={
              <ProtectedRoute>
                <ActionPlanView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pm"
            element={
              <ProtectedRoute roles={['portfolio_manager', 'admin']}>
                <PortfolioManagerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/security"
            element={
              <ProtectedRoute>
                <AccountSecurity />
              </ProtectedRoute>
            }
          />

          {/* Fallback: go to RoleGate which will handle redirects */}
          <Route path="*" element={<RoleGate />} />
        </Routes>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
